import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function SuccessStories() {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [activeStory, setActiveStory] = useState(0);

  const stories = [
    {
      challengeKey: 'success.story1Challenge',
      strategyKey: 'success.story1Strategy',
      executionKey: 'success.story1Execution',
      resultsKey: 'success.story1Results',
      quoteKey: 'success.story1Quote',
      nameKey: 'success.story1Name',
      roleKey: 'success.story1Role',
      image: 'https://readdy.ai/api/search-image?query=Professional%20businesswoman%20headshot%20portrait%20with%20warm%20lighting%20and%20clean%20white%20background%20confident%20expression%20modern%20corporate%20style%20high%20quality%20editorial%20photography&width=200&height=200&seq=testimonial-person-01&orientation=squarish',
    },
    {
      challengeKey: 'success.story2Challenge',
      strategyKey: 'success.story2Strategy',
      executionKey: 'success.story2Execution',
      resultsKey: 'success.story2Results',
      quoteKey: 'success.story2Quote',
      nameKey: 'success.story2Name',
      roleKey: 'success.story2Role',
      image: 'https://readdy.ai/api/search-image?query=Professional%20male%20entrepreneur%20headshot%20portrait%20with%20warm%20lighting%20clean%20white%20background%20modern%20business%20style%20confident%20expression%20editorial%20photography&width=200&height=200&seq=testimonial-person-02&orientation=squarish',
    },
    {
      challengeKey: 'success.story3Challenge',
      strategyKey: 'success.story3Strategy',
      executionKey: 'success.story3Execution',
      resultsKey: 'success.story3Results',
      quoteKey: 'success.story3Quote',
      nameKey: 'success.story3Name',
      roleKey: 'success.story3Role',
      image: 'https://readdy.ai/api/search-image?query=Professional%20female%20executive%20headshot%20portrait%20with%20warm%20lighting%20clean%20white%20background%20elegant%20business%20style%20sophisticated%20editorial%20photography&width=200&height=200&seq=testimonial-person-03&orientation=squarish',
    },
  ];

  const steps = [
    { key: 'success.challenge', value: 0 },
    { key: 'success.strategy', value: 1 },
    { key: 'success.execution', value: 2 },
    { key: 'success.results', value: 3 },
  ];

  const storyTabs = [
    { key: 'success.ecommerce', value: 0 },
    { key: 'success.saas', value: 1 },
    { key: 'success.professional', value: 2 },
  ];

  const story = stories[activeStory];

  const contentMap: Record<number, { titleKey: string; textKey: string }> = {
    0: { titleKey: 'success.challenge', textKey: story.challengeKey },
    1: { titleKey: 'success.strategy', textKey: story.strategyKey },
    2: { titleKey: 'success.execution', textKey: story.executionKey },
    3: { titleKey: 'success.results', textKey: story.resultsKey },
  };

  return (
    <section id="success-stories" className="py-20 md:py-28 bg-white overflow-hidden">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <ScrollReveal direction="up" duration={0.7}>
          <div className="text-center mb-14 md:mb-18">
            <span className="inline-block px-4 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-600 text-xs font-semibold tracking-wider uppercase mb-5">
              {t('success.tag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {t('success.title')}{' '}
              <span className="text-primary-500">{t('success.titleHighlight')}</span>
            </h2>
            <p className="text-gray-600 text-base md:text-lg max-w-xl mx-auto">
              {t('success.subtitle')}
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal direction="up" delay={0.15} duration={0.7}>
          <div className="flex justify-center gap-4 mb-10 flex-wrap">
            {storyTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => { setActiveStory(tab.value); setActiveStep(0); }}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeStory === tab.value
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t(tab.key)}
              </button>
            ))}
          </div>
        </ScrollReveal>

        {/* Timeline steps */}
        <ScrollReveal direction="up" delay={0.2} duration={0.7}>
          <div className="flex items-center justify-center gap-2 md:gap-4 mb-12 flex-wrap">
            {steps.map((step, i) => (
              <button
                key={step.key}
                onClick={() => setActiveStep(step.value)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer ${
                  step.value < activeStep
                    ? 'bg-gray-900 text-white'
                    : step.value === activeStep
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                  step.value < activeStep
                    ? 'bg-primary-500 text-white'
                    : step.value === activeStep
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {i + 1}
                </span>
                {t(step.key)}
              </button>
            ))}
          </div>
        </ScrollReveal>

        <div className="max-w-4xl mx-auto">
          <ScrollReveal direction="up" delay={0.3} duration={0.7}>
            <div className="bg-gray-50 rounded-2xl p-8 md:p-10 min-h-[200px] relative overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${activeStory}-${activeStep}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  {activeStep < 3 ? (
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-gray-900 mb-4">
                        {t(contentMap[activeStep].titleKey)}
                      </h3>
                      <p className="text-gray-600 text-base leading-relaxed">
                        {t(contentMap[activeStep].textKey)}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-gray-900 mb-4">
                        {t(contentMap[activeStep].titleKey)}
                      </h3>
                      <p className="text-gray-600 text-base leading-relaxed mb-8">
                        {t(contentMap[activeStep].textKey)}
                      </p>

                      <div className="flex items-start gap-5 p-6 bg-red-50 rounded-xl border border-red-200">
                        <img
                          src={story.image}
                          alt={t(story.nameKey)}
                          className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                        />
                        <div>
                          <p className="text-gray-800 text-sm italic leading-relaxed mb-3">
                            &ldquo;{t(story.quoteKey)}&rdquo;
                          </p>
                          <div className="font-semibold text-gray-900 text-sm">{t(story.nameKey)}</div>
                          <div className="text-gray-500 text-xs">{t(story.roleKey)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={0.4} duration={0.5}>
            <div className="flex justify-center gap-3 mt-8">
              <button
                onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                disabled={activeStep === 0}
                className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                aria-label={t('success.prev')}
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <button
                onClick={() => setActiveStep(Math.min(3, activeStep + 1))}
                disabled={activeStep === 3}
                className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white hover:bg-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                aria-label={t('success.next')}
              >
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}